import { useCallback, useMemo, useRef, useState } from 'react';
import { detectCardBrand, isValidCardNumber } from '../../../../../utils/cardValidation';
import { useMailOrderStore } from '../../../../../stores/useMailOrderStore';
import { useEProtect } from '../../../../billing/hooks/useEProtect';
import type { EProtectSuccessResponse } from '../../../../../types/runtime';

// mailorderApi submits cardExpMonth/cardExpYear directly as expmonth/expyear,
// so these stay zero-padded month + full 4-digit year (not BillingForm's 2-digit id).
function generateExpirationYears(): { display: string; id: string }[] {
  const startYear = new Date().getFullYear();
  return Array.from({ length: 12 }, (_, i) => {
    const year = String(startYear + i);
    return { display: year, id: year };
  });
}

const EXP_MONTHS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));

export interface WizardStep3PaymentProps {
  customerServicePhone: string;
  /** Called by this step after a valid payment method is confirmed. */
  onAdvance: () => void;
  onBack: () => void;
}

export function WizardStep3Payment({
  customerServicePhone,
  onAdvance,
  onBack,
}: WizardStep3PaymentProps) {
  const {
    paymentType,
    eprotectResponse,
    cardName,
    cardExpMonth,
    cardExpYear,
    nyDisclaimerAccepted,
    setPaymentType,
    setEprotectResponse,
    setCardName,
    setCardExpMonth,
    setCardExpYear,
    setCardLast4,
    setNyDisclaimerAccepted,
  } = useMailOrderStore();

  const cardInputRef = useRef<HTMLInputElement>(null);
  const [cardNumber, setCardNumber] = useState('');
  const [showBackConfirm, setShowBackConfirm] = useState(false);
  const expirationYears = useMemo(() => generateExpirationYears(), []);

  const cardBrand = detectCardBrand(cardNumber);
  const isCardNumberValid = cardNumber.length > 0 && isValidCardNumber(cardNumber);

  const handleSuccess = useCallback(
    (response: EProtectSuccessResponse) => {
      setEprotectResponse(response);
      onAdvance();
    },
    [setEprotectResponse, onAdvance]
  );

  const handleError = useCallback(() => {
    // error is surfaced via eprotect.error
  }, []);

  const eprotect = useEProtect(handleSuccess, handleError, { customerServicePhone });

  const isCardReady = paymentType === 'card' && !!eprotectResponse?.paypageRegistrationId;

  const handleContinue = () => {
    if (paymentType === 'check') {
      onAdvance();
      return;
    }
    // If already tokenized (user went Back and returned), advance directly
    if (isCardReady) {
      onAdvance();
      return;
    }

    // Card path — tokenize via eProtect, then store last 4
    eprotect.sendToEprotect(cardInputRef.current);
    setCardLast4(cardNumber.slice(-4));
  };

  const canContinue =
    paymentType === 'check' ||
    (paymentType === 'card' &&
      nyDisclaimerAccepted &&
      (isCardReady ||
        (cardName.trim() !== '' &&
          cardExpMonth !== '' &&
          cardExpYear !== '' &&
          isCardNumberValid)));

  return (
    <div>
      <h4 className="mb-3">Payment Method</h4>

      <div className="row">
        {/* ── Left column: payment selector + disclaimer + shipping ── */}
        <div className="col-12 col-md-6">
          {/* Payment type */}
          <p className="fw-bold mb-1">Payment</p>
          <div className="form-check mb-1">
            <input
              id="paymentCard"
              type="radio"
              className="form-check-input"
              name="paymentType"
              value="card"
              checked={paymentType === 'card'}
              onChange={() => setPaymentType('card')}
              style={{ marginRight: '5px' }}
            />
            <label htmlFor="paymentCard" className="form-check-label">
              Credit Card
            </label>
          </div>
          <div className="form-check mb-5" style={{ marginBottom: '20px' }}>
            <input
              id="paymentCheck"
              type="radio"
              className="form-check-input"
              name="paymentType"
              value="check"
              checked={paymentType === 'check'}
              onChange={() => setPaymentType('check')}
              style={{ marginRight: '5px' }}
            />
            <label htmlFor="paymentCheck" className="form-check-label">
              Check/Money Order/Add Payment Later
            </label>
          </div>

          {/* NY disclaimer — only shown for card */}
          {paymentType === 'card' && (
            <div className="form-check mb-4" style={{ marginBottom: '30px' }}>
              <input
                id="nyDisclaimer"
                type="checkbox"
                className="form-check-input"
                checked={nyDisclaimerAccepted}
                onChange={e => setNyDisclaimerAccepted(e.target.checked)}
                style={{ marginRight: '5px' }}
              />
              <label htmlFor="nyDisclaimer" style={{ fontWeight: 500, display: 'inline' }}>
                <em>
                  By checking this box, you acknowledge the following: Certain states require that
                  we advise patients about the risk of paying for medical services with a credit
                  card. Medical debts have protections under federal and state laws, specifically
                  the prohibitions against wage garnishment, property liens and reporting of medical
                  debt to credit bureaus. If you pay medical bills with a credit card, those
                  protections may not apply.{' '}
                  <strong>
                    By choosing to pay with a credit card, you acknowledge that you were notified of
                    the risks, and you agree to proceed with this payment method.
                  </strong>
                </em>{' '}
                <a
                  href="https://vytlone.com/wp-content/uploads/2026/02/FAQs-New-York-State-Notice-for-Patients-Paying-by-Credit-Card.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="NY credit card patient FAQ"
                >
                  &#9432;
                </a>
              </label>
            </div>
          )}

          {/* Shipping */}
          <div className="mb-3">
            <p className="fw-bold mb-1">Shipping</p>
            <p className="mb-0">
              To expedite shipping please call customer service at {customerServicePhone}.
            </p>
            <p className="mb-0">
              <em>Note: Expedited shipping will NOT rush prescription processing.</em>
            </p>
          </div>
        </div>

        {/* ── Right column: Credit Card Information panel ── */}
        {paymentType === 'card' && (
          <div className="col-12 col-md-6">
            <div className="p-3 rounded" style={{ backgroundColor: '#f5f5f5' }}>
              <p className="fw-bold mb-3">Credit Card Information</p>

              {isCardReady ? (
                <div className="alert alert-success d-flex align-items-center gap-2 py-2">
                  <span>&#10003;</span>
                  <span>Card information captured securely.</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary ms-auto"
                    onClick={() => {
                      useMailOrderStore.setState({ eprotectResponse: null });
                      eprotect.reset();
                    }}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <>
                  {/* Name on Card */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label htmlFor="wizardCardName" className="form-label fw-semibold">
                      Cardholder Name on Credit Card:
                    </label>
                    <input
                      id="wizardCardName"
                      type="text"
                      className="form-control"
                      value={cardName}
                      onChange={e => setCardName(e.target.value)}
                      autoComplete="cc-name"
                      disabled={eprotect.loading}
                    />
                  </div>

                  {/* Card Number */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label htmlFor="wizardCardNumber" className="form-label fw-semibold">
                      Card Number:
                    </label>
                    <input
                      ref={cardInputRef}
                      id="wizardCardNumber"
                      type="text"
                      className={`form-control${cardNumber.length > 0 ? (isCardNumberValid ? ' is-valid' : ' is-invalid') : ''}`}
                      value={cardNumber}
                      onChange={e => {
                        const digits = e.target.value.replace(/\D/g, '');
                        const brand = detectCardBrand(digits);
                        const maxLen = brand === 'AMEX' ? 15 : 16;
                        setCardNumber(digits.slice(0, maxLen));
                      }}
                      maxLength={16}
                      autoComplete="cc-number"
                      disabled={eprotect.loading}
                    />
                    {cardNumber.length > 0 && cardBrand && isCardNumberValid && (
                      <div className="valid-feedback d-block">{cardBrand}</div>
                    )}
                    {cardNumber.length > 0 && !cardBrand && (
                      <div className="invalid-feedback error-msg d-block">
                        Please enter a valid credit card number.
                      </div>
                    )}
                    {cardNumber.length > 0 && cardBrand && !isCardNumberValid && (
                      <div className="invalid-feedback error-msg d-block">
                        Please enter a valid credit card number.
                      </div>
                    )}
                    {eprotect.error && (
                      <div className="text-danger small mt-1">{eprotect.error}</div>
                    )}
                  </div>

                  {/* Expiration Date */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label htmlFor="wizardExpMonth" className="form-label fw-semibold d-block">
                      Expiration Date:
                    </label>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'nowrap',
                        alignItems: 'flex-start',
                        gap: 8,
                      }}
                    >
                      <div>
                        <select
                          id="wizardExpMonth"
                          className="form-control"
                          style={{ width: 70 }}
                          value={cardExpMonth}
                          onChange={e => setCardExpMonth(e.target.value)}
                          autoComplete="cc-exp-month"
                          disabled={eprotect.loading}
                        >
                          <option value="">MM</option>
                          {EXP_MONTHS.map(month => (
                            <option key={month} value={month}>
                              {month}
                            </option>
                          ))}
                        </select>
                        <small className="text-muted">MM</small>
                      </div>
                      <div>
                        <select
                          id="wizardExpYear"
                          className="form-control"
                          style={{ width: 90 }}
                          value={cardExpYear}
                          onChange={e => setCardExpYear(e.target.value)}
                          autoComplete="cc-exp-year"
                          disabled={eprotect.loading}
                        >
                          <option value="">YYYY</option>
                          {expirationYears.map(year => (
                            <option key={year.id} value={year.id}>
                              {year.display}
                            </option>
                          ))}
                        </select>
                        <small className="text-muted">YYYY</small>
                      </div>
                    </div>
                  </div>


                </>
              )}
            </div>
          </div>
        )}

        {/* Check/Money Order message */}
        {paymentType === 'check' && (
          <div className="col-12 col-md-6">
            <div className="p-3 rounded" style={{ backgroundColor: '#f5f5f5' }}>
              <p className="fw-bold mb-2">Check/Money Order/Add Payment Later Information</p>
              <p className="mb-0">
                When paying by check or money order, your prescription order will be shipped when
                payment is received. Please enclose the correct co-payment to avoid delays
                processing your request. If you need assistance calculating your co-payment, call
                Customer Service at {customerServicePhone}.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="d-flex gap-2 mt-4">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            const isCardDirty =
              paymentType === 'card' &&
              (cardName.trim() !== '' ||
                cardNumber !== '' ||
                cardExpMonth !== '' ||
                cardExpYear !== '' ||
                nyDisclaimerAccepted ||
                eprotectResponse !== null);
            if (isCardDirty) {
              setShowBackConfirm(true);
            } else {
              onBack();
            }
          }}
        >
          Back
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleContinue}
          disabled={eprotect.loading || !canContinue}
          style={{ float: 'right' }}
        >
          {eprotect.loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              Processing…
            </>
          ) : (
            'Continue'
          )}
        </button>
      </div>

      {/* Unsaved-changes confirmation dialog — mirrors the RefillsPage sidebar guard */}
      {showBackConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100%',
            width: '100%',
            background: 'rgba(0, 0, 0, 0.7)',
            zIndex: 1040,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="wizard-step3-unsaved-dialog-title"
          onKeyDown={e => {
            if (e.key === 'Escape') {
              setShowBackConfirm(false);
            }
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 8,
              boxShadow: '0 3px 3px rgba(0, 0, 0, .3)',
              padding: 25,
              width: 550,
              maxWidth: '90%',
              textAlign: 'center',
            }}
          >
            <h3
              id="wizard-step3-unsaved-dialog-title"
              style={{ textTransform: 'uppercase', fontWeight: 'bold', marginTop: 0 }}
            >
              Unsaved Changes
            </h3>
            <p style={{ marginBottom: 15 }}>
              The form contains unsaved changes, are you sure you want to leave this screen?
            </p>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 10,
                paddingBottom: 15,
                borderBottom: '5px solid #FF471A',
              }}
            >
              <button
                type="button"
                style={{
                  backgroundColor: '#FF471A',
                  color: '#FAFAFA',
                  border: 'none',
                  borderRadius: 4,
                  padding: '8px 20px',
                  cursor: 'pointer',
                }}
                onClick={() => setShowBackConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{
                  backgroundColor: '#FFD24C',
                  color: '#1E1E1E',
                  border: 'none',
                  borderRadius: 4,
                  padding: '8px 20px',
                  minWidth: 75,
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setShowBackConfirm(false);
                  onBack();
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
